import { useEffect, useState } from 'react'
import { redirect, useLoaderData, useNavigate } from 'react-router'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { BranchPageHeading } from '@/components/subscription/branch-page-heading'
import { Panel } from '@/components/subscription/panel'
import { formatDate, formatTime } from '@/lib/shipment'
import { findShippingStatus } from '@/lib/order'
import { createClient } from '@/api-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import { SubscriptionSchedules } from '@/api-client/SubscriptionSchedules'
import { Subscription, SubscriptionInventory, SubscriptionSchedule } from '@/api-client/data-contracts'

export const loader = async () => {
  const subscriptions = await fetchSubscriptions()
  const subscription = subscriptions.data.data[0]

  if (subscription) {
    const address = await fetchAddress(subscription)
    const schedules = await fetchSchedules(address.data.data.attributes.postal_code)
    const orders = await fetchOrders(subscription)

    return [subscriptions, schedules, orders] as const
  } else {
    return redirect('/subscription/rose/mypage')
  }
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions({ category: 'rose' })
}

function fetchAddress(subscription: Subscription) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdAddress(subscription.id)
}

function fetchSchedules(postalCode?: string) {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ postal_code: postalCode, category: 'rose', upcoming: '1', limit: '9' })
}

function fetchOrders(subscription: Subscription) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdOrders(subscription.id, { include: ['order_items.shipping_statuses'] })
}

function fetchPlannedSchedules(subscription: Subscription, perMonth: number, weekday: number) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdSchedulesPlan(subscription.id, {
    per_month: perMonth.toString(),
    weekday: weekday.toString(),
  })
}

function formatDeliverWeekday(schedule: SubscriptionSchedule) {
  return format(new Date(1000 * schedule.attributes.deliver_at[0]), 'E', { locale: ja })
}

export const SubscriptionRoseMypageSchedule: React.FC = () => {
  const [
    {
      data: { data: subscriptions, included: subscriptionsIncluded },
    },
    {
      data: { data: schedules },
    },
    {
      data: { data: orders, included: ordersIncluded },
    },
  ] = useLoaderData<typeof loader>()
  const navigate = useNavigate()
  const subscription = subscriptions[0]
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [futureSchedules, setFutureSchedules] = useState(() => {
    return subscription.relationships.inventories.data
      .map((inventory: SubscriptionInventory) => schedules.find((s) => s.relationships.inventory.data.id === inventory.id))
      .sort((a, b) => a.attributes.deliver_at[0] - b.attributes.deliver_at[0])
  })
  const options = Object.groupBy(schedules, (schedule) => formatDeliverWeekday(schedule))
  const weekdays = Object.keys(options).sort((w) => (w === '水' || w === '木' ? -1 : 1))
  const [perMonth, setPerMonth] = useState(subscriptionsIncluded.filter((item) => item.type === 'subscription_inventory').length)
  const [weekday, setWeekday] = useState(() => {
    const schedule = schedules.find((s) => subscriptionsIncluded.some((item) => s.relationships.inventory.data.id === item.id))
    const deliverAtDay = new Date(schedule?.attributes.deliver_at[0] * 1000).getDay()

    return deliverAtDay === 2 || deliverAtDay === 3 ? 2 : 5
  })
  const latestShipping = findShippingStatus(orders[0], ordersIncluded)
  const latestShippingStatus = latestShipping?.attributes?.status

  const frequencies = [
    { label: '月1回', value: 1 },
    { label: '月2回', value: 2 },
    { label: '月4回', value: 4 },
  ]

  useEffect(() => {
    fetchPlannedSchedules(subscription, perMonth, weekday)
      .then((res) => res.data.data)
      .then((planned) => setFutureSchedules(planned))
  }, [subscription, perMonth, weekday])

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = async () => {
    setIsSubmitting(true)

    try {
      await createClient(Subscriptions).putSubscriptionsSubscriptionIdContract(subscription.id, { per_month: perMonth.toString(), weekday: weekday.toString() })
      await navigate('/subscription/rose/mypage', { state: { message: '受け取りスケジュールを変更しました' } })
    } catch (error) {
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="w-full space-y-8 pt-4 pb-18">
      <BranchPageHeading main="受け取りスケジュール変更" />

      <div className="space-y-4">
        <Panel padding="m">
          <p className="text-[14px] font-medium text-black mb-1">受け取りたい曜日を選んでください</p>
          <div className="flex gap-2">
            {weekdays.map((day) => {
              const value = day === '水' || day === '木' ? 2 : 5

              return (
                <button
                  key={day}
                  onClick={() => setWeekday(value)}
                  className={`w-10 h-10 rounded-full text-[13px] font-medium transition-colors ${
                    weekday === value ? 'bg-black text-white' : 'border border-gray text-main hover:border-black'
                  }`}
                >
                  {day}
                </button>
              )
            })}
          </div>
        </Panel>

        <section className="bg-white rounded-2xl p-6">
          <p className="text-[14px] font-medium text-black mb-3">受け取りたい頻度を選んでください</p>
          <div className="flex gap-2 flex-wrap">
            {frequencies.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setPerMonth(value)}
                className={`px-4 py-2 rounded-full text-[13px] font-medium transition-colors ${
                  perMonth === value ? 'bg-black text-white' : 'border border-gray text-main hover:border-black'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="mt-3 text-[12px] text-dark-gray">※1配送ごとに¥3,520がかかります</p>
        </section>

        <section className="bg-white rounded-2xl p-6">
          <p className="text-[14px] font-medium text-black mb-3">変更後のお届け予定</p>
          <div className="space-y-1">
            {['not_shipped', 'preparation', 'transit'].includes(latestShippingStatus) && (
              <div className="flex items-center gap-1">
                <span className="text-[19px] font-bold text-black leading-relaxed">{formatDate(1000 * latestShipping.attributes.deliver_at, 'M/d（E）')}</span>
                <span className="inline-block bg-[#eeeeee] rounded-sm px-0.5 py-1.5 text-[12px] font-bold">{latestShipping.attributes.status_label}</span>
              </div>
            )}

            {futureSchedules.map((schedule, i) => (
              <p key={schedule.id} className={i === 0 ? 'text-[19px] font-bold text-black' : 'text-[15px] font-bold text-black'}>
                {formatDate(schedule.attributes.deliver_at[0] * 1000, 'M/d（E）')}
              </p>
            ))}

            <p className="mt-2 text-[13px] text-dark-gray">
              月{perMonth}回・{formatDeliverWeekday(futureSchedules[0])}曜日にお届け
            </p>
          </div>
        </section>

        <div className="pt-2 flex flex-col gap-3">
          <p className="text-center text-[13px] text-dark-gray">
            {formatTime(1000 * subscription.attributes.skip_deadline)}までに保存すると、次回のお届けに反映されます
          </p>

          <button
            type="button"
            disabled={isSubmitting}
            className="w-full py-5 bg-black text-white text-[15px] font-medium rounded-full cursor-pointer hover:bg-main disabled:bg-main transition-colors"
            onClick={handleSubmit}
          >
            保存する
          </button>
        </div>
      </div>
    </div>
  )
}
